import type { CustomFieldDefinition, Resource } from '__types__';
import { resolveCustomFieldValue } from 'v5/core/custom-fields/resolveCustomFieldValue';
import customHelper from 'v1/helpers/customFieldHelper';
import { displayCurrency } from 'v1/helpers/currencyHelper';
import { displayDateRange } from 'v1/helpers/byType/dateHelper';
import {
  displayDimension,
  displayHeight,
  displayMeasurement
} from 'v1/helpers/measurements/measurementsHelper';
import moment from 'moment-timezone';
import { publicInstance } from 'v5/i18n.public';
import { unitFieldUtils } from 'v4/shared/components/forms/input/unit-field-utils';
import { maybe } from '@passionware/monads';
import type { ShortlistQrSetting } from 'v5/api/shortlist-board/shortlistBoard.api';
import type { ShortlistTypeFields } from './shortlistDisplayFields.logic';

/** The parts of the code that come from the team's custom fields, in the order they are written. */
export const QR_SLOTS = [
  'gender',
  'bust',
  'waist',
  'height',
  'shoe',
  'bra_size'
] as const satisfies ReadonlyArray<keyof ShortlistQrSetting['mapping']>;
export type QrSlot = (typeof QR_SLOTS)[number];

export type ShortlistQrValue =
  | {
      type: 'ready';
      /** What the scanner reads: parts joined by "_", `null` for an empty one. */
      value: string;
      emptySlots: QrSlot[];
      /** Empty slots whose field the person's resource type requires. */
      missingSlots: QrSlot[];
    }
  /** A mapped field was deleted: nothing is drawn rather than a wrong code. */
  | { type: 'cannot-build'; deletedSlots: QrSlot[] };

type SlotValue = string | number | null;

function optionValue(
  definition: CustomFieldDefinition,
  optionId: number | null | undefined
): string {
  if (!optionId) return '';
  return definition.options.find(option => option.id === optionId)?.value ?? '';
}

function measurementInUnit(
  raw: { amount?: string | null; unit?: string | null } | null,
  unit: ShortlistQrSetting['unit']
): number | null {
  if (maybe.isAbsent(raw?.unit) || maybe.isAbsent(raw?.amount)) return null;
  const unitValue = unitFieldUtils.getUnitValueFromServerValue(
    raw.amount,
    raw.unit as Parameters<typeof unitFieldUtils.getUnitValueFromServerValue>[1]
  );
  const imperial = unitFieldUtils.getImperialValueFromUnitValue(unitValue);
  return unitFieldUtils.getNumericalValue(
    unitFieldUtils.getUnitValueFromImperial(maybe.fromTruthy(imperial), unit)
  );
}

/** The slot's field as the legacy code writes it. */
function slotValueOf(
  definition: CustomFieldDefinition,
  contact: Resource,
  workspaceId: number | null,
  unit: ShortlistQrSetting['unit']
): SlotValue {
  const row = resolveCustomFieldValue(
    contact.custom_fields,
    definition.id,
    workspaceId
  );
  const value = row ? customHelper.getValue(row, definition.data_type) : null;
  switch (definition.data_type) {
    case 'MEASUREMENT_NEW':
      return measurementInUnit(value, unit);
    case 'SINGLE_SELECT':
      return optionValue(definition, value);
    case 'MULTI_SELECT':
      return (value ?? [])
        .map((id: number) => optionValue(definition, id))
        .filter(Boolean)
        .join(', ');
    case 'HEIGHT':
      return displayHeight(value);
    case 'MEASUREMENT':
      return value && value.amount ? displayMeasurement(value) : null;
    case 'BOOLEAN':
      switch (value) {
        case true:
          return publicInstance.t('ds:TernaryToggle.yes');
        case false:
          return publicInstance.t('ds:TernaryToggle.no');
        default:
          return publicInstance.t('ds:TernaryToggle.emptyValue');
      }
    // the legacy code writes a date as the page shows it, "Invalid date" for none
    case 'DATE':
      return moment(value).format('Do MMM YYYY');
    case 'DATE_RANGE':
      return displayDateRange(value);
    case 'CURRENCY':
      return displayCurrency(value?.amount, value?.currency);
    case 'DIMENSION_2D':
    case 'DIMENSION_3D': {
      const text = displayDimension(value);
      return text && text !== '' ? text : null;
    }
    // the rest as stored, as the legacy code does (a URL it failed on; the code is built without it)
    default:
      return value;
  }
}

const NULL_PART = 'null';

/**
 * The text of a contact's QR code, as the legacy shortlist wrote it: first name, last name,
 * groups, the mapped fields and the guid, joined by "_". A slot nobody mapped reads `null` and
 * stays silent; a mapped one whose definition is gone, or whose type cannot be written, makes the
 * whole code unbuildable.
 */
export function buildShortlistQrValue(input: {
  contact: Resource;
  definitions: ReadonlyArray<CustomFieldDefinition>;
  config: ShortlistQrSetting;
  workspaceId: number | null;
  typeFields: ShortlistTypeFields;
}): ShortlistQrValue {
  const { contact, definitions, config, workspaceId, typeFields } = input;
  const deletedSlots: QrSlot[] = [];
  const parts = new Map<QrSlot, SlotValue>();
  for (const slot of QR_SLOTS) {
    const definitionId = config.mapping[slot];
    if (definitionId === null) {
      parts.set(slot, null);
      continue;
    }
    const definition = definitions.find(({ id }) => id === definitionId);
    if (definition === undefined) {
      deletedSlots.push(slot);
      continue;
    }
    parts.set(slot, slotValueOf(definition, contact, workspaceId, config.unit));
  }
  if (deletedSlots.length > 0) return { type: 'cannot-build', deletedSlots };

  const fixed = (text: string | null | undefined) => text || NULL_PART;
  const text = (slot: QrSlot) => parts.get(slot) || NULL_PART;
  const value = [
    fixed(contact.first_name),
    fixed(contact.last_name),
    fixed((contact.groups ?? []).map(group => group.name).join(', ')),
    ...QR_SLOTS.map(text),
    fixed(contact.guid)
  ].join('_');
  const emptySlots = QR_SLOTS.filter(slot => !parts.get(slot));
  return {
    type: 'ready',
    value,
    emptySlots,
    missingSlots: emptySlots.filter(slot => {
      const id = config.mapping[slot];
      return (
        id !== null &&
        typeFields.type === 'known' &&
        typeFields.requiredCustomFieldIds.has(id)
      );
    })
  };
}

/** What the QR code field shows: a code, or why there is none yet. */
export type ShortlistQrFieldState =
  | { type: 'loading' }
  /** The team has not chosen what the code is made of; an editor who may set it up is told. */
  | { type: 'not-set-up' }
  | ShortlistQrValue;

/** Whether, and for whom, the QR code field is drawn. */
export type ShortlistQrSource =
  /** The feature is off: the field draws nothing, as before it existed. */
  | { type: 'off' }
  | { type: 'loading' }
  | {
      type: 'on';
      setting: ShortlistQrSetting | null;
      /** An editor also sees what is missing and where to set the code up; a viewer only the code. */
      audience:
        | { type: 'viewer' }
        | { type: 'editor'; setupHref: string | null };
    };

/** The code as a client sees it: there when the server sent the setting, which it does only for a shortlist that shows it. */
export function qrSourceOfViewer(
  setting: ShortlistQrSetting | null
): ShortlistQrSource {
  return setting === null
    ? { type: 'off' }
    : { type: 'on', setting, audience: { type: 'viewer' } };
}

/**
 * The code on the team's board: off with the feature, a placeholder while the flag is read,
 * otherwise as an editor sees it. `setupHref`: the team's QR code setting, `null` for someone
 * who may not open it.
 */
export function qrSourceOfEditor(input: {
  setting: ShortlistQrSetting | null;
  /** `undefined` while the flag is being read. */
  featureOn: boolean | undefined;
  setupHref: string | null;
}): ShortlistQrSource {
  const { setting, featureOn, setupHref } = input;
  if (featureOn === undefined) return { type: 'loading' };
  return featureOn
    ? { type: 'on', setting, audience: { type: 'editor', setupHref } }
    : { type: 'off' };
}
